(() => {
  if (window.__culgeCuratedGalleryLoaded) return;
  window.__culgeCuratedGalleryLoaded = true;

  class CulgeCuratedGallery {
    constructor(section) {
      if (!section || section.dataset.galleryInitialized === 'true') return;

      this.section = section;
      this.slides = Array.from(section.querySelectorAll('[data-gallery-slide]'));
      this.dots = Array.from(section.querySelectorAll('[data-gallery-dot]'));
      this.previousButton = section.querySelector('[data-gallery-prev]');
      this.nextButton = section.querySelector('[data-gallery-next]');
      this.currentIndex = Math.max(
        0,
        this.slides.findIndex((slide) => slide.classList.contains('is-active'))
      );
      this.autoplay = section.dataset.autoplay === 'true';
      this.autoplaySpeed = Number(section.dataset.autoplaySpeed) || 6000;
      this.timer = null;

      if (this.slides.length < 2) {
        section.dataset.galleryInitialized = 'true';
        return;
      }

      this.onPrevious = () => this.show(this.currentIndex - 1, true);
      this.onNext = () => this.show(this.currentIndex + 1, true);
      this.onKeydown = (event) => {
        if (event.key === 'ArrowLeft') this.onPrevious();
        if (event.key === 'ArrowRight') this.onNext();
      };
      this.onVisibilityChange = () => {
        if (document.hidden) this.stop();
        else this.start();
      };

      this.previousButton?.addEventListener('click', this.onPrevious);
      this.nextButton?.addEventListener('click', this.onNext);
      this.section.addEventListener('keydown', this.onKeydown);
      this.section.addEventListener('mouseenter', () => this.stop());
      this.section.addEventListener('mouseleave', () => this.start());
      this.section.addEventListener('focusin', () => this.stop());
      this.section.addEventListener('focusout', () => this.start());
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => this.show(index, true));
      });

      section.dataset.galleryInitialized = 'true';
      this.show(this.currentIndex, false);
      this.start();
    }

    show(index, userInitiated = false) {
      const total = this.slides.length;
      this.currentIndex = (index + total) % total;

      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === this.currentIndex;
        slide.hidden = !active;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      this.dots.forEach((dot, dotIndex) => {
        const active = dotIndex === this.currentIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
        dot.tabIndex = active ? 0 : -1;
      });

      if (userInitiated) this.restart();
    }

    start() {
      if (!this.autoplay || this.slides.length < 2 || document.hidden) return;
      this.stop();
      this.timer = window.setInterval(() => this.show(this.currentIndex + 1), this.autoplaySpeed);
    }

    stop() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }

    restart() {
      this.stop();
      this.start();
    }

    destroy() {
      this.stop();
      this.previousButton?.removeEventListener('click', this.onPrevious);
      this.nextButton?.removeEventListener('click', this.onNext);
      this.section.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      delete this.section.dataset.galleryInitialized;
    }
  }

  const instances = new WeakMap();

  const initialize = (root = document) => {
    const sections = [];
    if (root.matches?.('[data-culge-gallery]')) sections.push(root);
    root.querySelectorAll?.('[data-culge-gallery]').forEach((section) => sections.push(section));

    sections.forEach((section) => {
      const gallery = new CulgeCuratedGallery(section);
      instances.set(section, gallery);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.matches?.('[data-culge-gallery]')
      ? event.target
      : event.target.querySelector('[data-culge-gallery]');
    instances.get(section)?.destroy();
  });

  document.addEventListener('shopify:block:select', (event) => {
    const section = event.target.closest('[data-culge-gallery]');
    const selectedSlide = event.target.closest('[data-gallery-slide]');
    const gallery = instances.get(section);
    if (!gallery || !selectedSlide) return;

    gallery.show(Number(selectedSlide.dataset.gallerySlide), false);
    gallery.stop();
  });
})();
